import { EntityIdAliasPrefixes } from '@abb-procure/constants';
import { AvailableStatusTransitionModel } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { FilterPipe } from '../../pipes/filter.pipe';
import { DialogService } from '../../services/dialog.service';
import { EntityService } from '../../services/entity.service';
import { FormMode, FormModeService } from '../../services/form-mode.service';
import { StatusWorkflowDialogService } from '../../services/status-workflow-dialog.service';
import { ButtonGroup } from '../button-group/button-group.component';
import { Button } from '../button/button.component';

export enum TestIds {
  Edit = 'PageHeaderEditButton',
  Cancel = 'PageHeaderCancelButton',
  Save = 'PageHeaderSaveButton',
  Toggle = 'PageHeaderToggleButton',
  Delete = 'PageHeaderDeleteButton',
}

export interface ReRouteConfig {
  prompt: string;
  route: string;
  tooltip?: string;
}
@Component({
  selector: 'procure-page-header-actionbar',
  templateUrl: './page-header-actionbar.component.html',
  styleUrl: './page-header-actionbar.component.scss',
  imports: [
    MatIcon,
    Button,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    FilterPipe,
    ButtonGroup,
    RouterLink,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHeaderActionbar {
  readonly availableStatusTransitions = input<AvailableStatusTransitionModel[]>(
    [],
  );

  readonly saveAsDraft = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly evaluation = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly pristine = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  readonly isSaving = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly isDeleting = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly isTogglingFavorite = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly canEdit = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly canSave = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly canDelete = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly canReview = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly fileUploadsPending = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly invalid = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly reviewInvalid = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly allowComplexEdit = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly allowSaveWithoutNotifying = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly hideMutateActions = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly entityType = input<EntityIdAliasPrefixes>();

  readonly name = input<string | null>();

  readonly reRoute = input<ReRouteConfig>();

  readonly transitionStatus = output<number>();
  readonly reopen = output<number>();
  readonly edit = output<void>();
  readonly finishReview = output<void>();
  readonly editComplex = output<void>();
  readonly saveAction = output<boolean>();
  readonly submitAction = output<void>();
  readonly cancelAction = output<void>();
  readonly deleteAction = output<void>();

  readonly TestIds = TestIds;

  readonly isPerformingAction = computed(
    () => this.isDeleting() || this.isSaving() || this.isTogglingFavorite(),
  );

  readonly formModeService = inject(FormModeService);
  readonly entityService = inject(EntityService);

  private readonly dialogService = inject(DialogService);

  private readonly statusWorkflowDialogService = inject(
    StatusWorkflowDialogService,
  );

  private readonly destroyRef = inject(DestroyRef);

  onEdit(): void {
    this.formModeService.setFormMode(FormMode.Edit);
    this.edit.emit();
  }

  onReview(): void {
    this.formModeService.setFormMode(FormMode.Review);
  }

  onFinishReview(): void {
    this.finishReview.emit();
  }

  onEditComplex(): void {
    this.formModeService.setFormMode(FormMode.EditComplex);
    this.edit.emit();
  }

  onSave(notify: boolean): void {
    this.saveAction.emit(notify);
  }

  onDraftSave(toInProgress: boolean): void {
    this.saveAction.emit(toInProgress);
  }

  onSubmit(): void {
    this.submitAction.emit();
  }

  onDelete(): void {
    const entityNumber = this.entityService.getEntityNumber()
      ? `[${this.entityService.getEntityNumber()}]`
      : '';

    this.dialogService
      .showConfirmDialog({
        text: `Are you sure you want to delete "${[entityNumber, this.name()].join(' ')}"?\n\nNote: Deleting this entity will remove all related entities. This action cannot be undone.`,
        primaryAction: 'Delete',
        secondaryAction: 'Cancel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!response) {
          return;
        }

        this.deleteAction.emit();
      });
  }

  onCancel(): void {
    if (this.pristine()) {
      this.cancelAction.emit();
      this.formModeService.setFormMode(FormMode.Show);

      return;
    }

    this.dialogService
      .showConfirmDialog()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response) {
          this.cancelAction.emit();
          this.formModeService.setFormMode(FormMode.Show);
        }
      });
  }

  showWorkflowDialog(): void {
    const entityType = this.entityType();

    if (!entityType) {
      return;
    }

    this.statusWorkflowDialogService.openStatusWorkflowDialog({
      entityType,
    });
  }
}
